import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, CheckCircle, Circle, Warning, Trash } from "@phosphor-icons/react";
import { FieldLabel } from "../shared/FieldHint";
import { PayScheduleBanner } from "../shared/PayScheduleBanner";
import { ComingSoonSection } from "../shared/ComingSoonSection";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const LINK_LABELS = { funding: "Personal / Business Funding", trust: "Trust" };

function OnboardingChecklist() {
  const { user } = useAuth();
  const [agreementSigned, setAgreementSigned] = useState(null);
  const [paymentSubmitted, setPaymentSubmitted] = useState(null);

  useEffect(() => {
    api.get("/manager/agreement").then(({ data }) => setAgreementSigned(data.signed)).catch(() => setAgreementSigned(false));
    api.get("/manager/payment-info").then(({ data }) => setPaymentSubmitted(!!data)).catch(() => setPaymentSubmitted(false));
  }, []);

  const items = [
    { label: "Set your password", done: !user?.must_change_password },
    { label: "Sign the Manager Partner Agreement", done: agreementSigned },
    { label: "Submit payment & tax info", done: paymentSubmitted },
  ];
  if (items.every((i) => i.done)) return null;

  return (
    <div className="bg-igc-surface border border-igc-gold/25 p-5 mb-8" data-testid="manager-onboarding-checklist">
      <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3">Getting Started</h2>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-sm" data-testid={`onboarding-item-${item.label.replace(/\s+/g, "-").toLowerCase()}`}>
            {item.done ? <CheckCircle size={16} weight="fill" className="text-igc-success" /> : <Circle size={16} className="text-igc-muted" />}
            <span className={item.done ? "text-igc-muted line-through" : "text-igc-cream"}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NudgeHistory() {
  const [nudges, setNudges] = useState([]);

  const fetchNudges = () => {
    api.get("/manager/nudge-history").then(({ data }) => setNudges(data)).catch(() => {});
  };

  useEffect(() => {
    fetchNudges();
  }, []);

  const endTask = async (nudgeId) => {
    try {
      await api.post(`/manager/nudges/${nudgeId}/resolve`, {});
      toast.success("Nudge cleared");
      fetchNudges();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteNudge = async (nudgeId) => {
    if (!window.confirm("Permanently delete this nudge from your history? This cannot be undone.")) return;
    try {
      await api.delete(`/manager/nudges/${nudgeId}`);
      toast.success("Nudge deleted");
      fetchNudges();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteAllNudges = async () => {
    if (!window.confirm("Permanently delete your ENTIRE nudge history? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/manager/nudges");
      toast.success(data.message);
      fetchNudges();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (nudges.length === 0) return null;

  return (
    <div className="bg-igc-surface border border-igc-gold/25 p-5 mb-8" data-testid="manager-nudge-history">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg text-igc-cream tracking-tight">Nudge History</h2>
        <button
          data-testid="manager-nudge-clear-all-button"
          onClick={deleteAllNudges}
          title="Permanently deletes your entire nudge history at once. Cannot be undone."
          className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-igc-muted hover:text-igc-danger transition-colors"
        >
          <Trash size={12} weight="bold" /> Clear All
        </button>
      </div>
      <div className="space-y-2">
        {nudges.map((n) => (
          <div key={n.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`nudge-item-${n.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              {n.resolved ? <CheckCircle size={16} weight="fill" className="text-igc-success shrink-0" /> : <Warning size={16} weight="fill" className="text-igc-goldText shrink-0" />}
              <span className={`truncate ${n.resolved ? "text-igc-muted line-through" : "text-igc-cream"}`}>{n.message}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!n.resolved && (
                <button
                  data-testid={`nudge-end-task-button-${n.id}`}
                  onClick={() => endTask(n.id)}
                  title="Marks this nudge as done and removes it from your active view — it stays in your history forever."
                  className="text-[10px] px-2 py-1 uppercase tracking-wide border border-igc-gold/30 text-igc-goldText hover:bg-igc-gold hover:text-black shrink-0"
                >
                  End Task
                </button>
              )}
              <button
                data-testid={`nudge-delete-button-${n.id}`}
                onClick={() => deleteNudge(n.id)}
                title="Permanently deletes this nudge from your history — unlike End Task, this cannot be undone."
                className="text-igc-muted hover:text-igc-danger shrink-0"
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManagerOverviewTab() {
  const [links, setLinks] = useState(null);
  const [commission, setCommission] = useState(null);

  useEffect(() => {
    const fetchData = () => {
      Promise.all([api.get("/manager/referral-links"), api.get("/manager/commission-summary")])
        .then(([l, c]) => {
          setLinks(l.data);
          setCommission(c.data);
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    };
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, []);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy link"));
  };

  const allLinks = links;

  return (
    <div data-testid="manager-overview-tab">
      <OnboardingChecklist />
      <NudgeHistory />
      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1" title="Your personal referral links — anyone who signs up through these lands directly under you.">Your Referral Links</h2>
      <p className="text-igc-muted text-xs mb-4">Share these so clients or new affiliates land straight under you.</p>
      <div className="grid md:grid-cols-2 gap-3 mb-10">
        {allLinks && Object.entries(allLinks).map(([key, url]) => (
          <div key={key} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-3" data-testid={`manager-referral-link-${key}`}>
            <div className="overflow-hidden">
              <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-1">{LINK_LABELS[key] || key}</p>
              <p className="text-igc-cream text-xs truncate">{url}</p>
            </div>
            <button data-testid={`manager-copy-link-${key}`} onClick={() => copyLink(url)} title="Copies this referral link to your clipboard." className="text-igc-goldText hover:text-igc-cream shrink-0">
              <Copy size={16} weight="duotone" />
            </button>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4" title="Your personal commission earned directly, your automatic override on your team's deals, and the total combined.">Commission Summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
        <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-direct-earned">
          <FieldLabel hint="Commission you earned directly on your own closed referrals." testId="manager-direct-earned-hint" className="mb-2 block">Direct Earned</FieldLabel>
          <p className="font-serif text-2xl text-igc-success">${(commission?.direct_earned || 0).toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-override-earned">
          <FieldLabel hint="Your automatic earnings on your team's closed deals. This is calculated for you — no action needed." testId="manager-override-earned-hint" className="mb-2 block">Override Earned</FieldLabel>
          <p className="font-serif text-2xl text-igc-goldText">${(commission?.override_earned || 0).toLocaleString()}</p>
          <p className="text-igc-muted text-[10px] mt-1">{((commission?.override_rate || 0) * 100).toFixed(0)}% rate</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-total-earned">
          <FieldLabel hint="Direct Earned plus Override Earned combined." testId="manager-total-earned-hint" className="mb-2 block">Total Earned</FieldLabel>
          <p className="font-serif text-2xl text-igc-cream">${(commission?.total_earned || 0).toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-sub-affiliate-count">
          <FieldLabel hint="Reps currently on your team." testId="manager-sub-affiliate-count-hint" className="mb-2 block">Reps</FieldLabel>
          <p className="font-serif text-2xl text-igc-cream">{commission?.sub_affiliate_count || 0}</p>
        </div>
      </div>

      <div className="mt-8 max-w-3xl">
        <PayScheduleBanner />
      </div>

      <div className="mt-8 max-w-3xl">
        <p className="text-igc-muted text-xs uppercase tracking-widest mb-3">What's Next For Your Team</p>
        <ComingSoonSection />
      </div>
    </div>
  );
}
